import { useCallback, useRef } from 'react';

import { SORT_ACTION_MAPPING } from '../../../shared/constants';
import { type ActiveFiltersState } from '../../../shared/types';
import { useLazyGetInsightsDetailsQuery } from '../data-layer/violationsTabApi';
import { type SortState } from '../data-layer/violationsTabTypes';
import { getFilterPayload } from '../utils';

const useGetInsights = () => {
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [fetchInsights, { isError, isLoading }] =
    useLazyGetInsightsDetailsQuery();

  const handleFetchInsights = useCallback(
    async (
      activeFilters: ActiveFiltersState[],
      tabInAction: string | null,
      searchKey: string | null,
      sortInAction: SortState
    ) => {
      abortRef.current?.('New Search');

      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = {
        filters: getFilterPayload(
          activeFiltersMap,
          tabInAction ?? undefined,
          searchKey
        ),
        sort: {
          field: sortInAction?.value ?? 'sodkey',
          order:
            SORT_ACTION_MAPPING[
              sortInAction?.order ?? SORT_ACTION_MAPPING.DESC
            ],
        },
      };

      try {
        const controller = fetchInsights(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error('Failed fetching insights');
      }
    },
    [fetchInsights]
  );

  return { handleFetchInsights, isError, isLoading };
};

// eslint-disable-next-line import/prefer-default-export
export { useGetInsights };
